// src/components/TokenSelectList.js
import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
// import axios from 'axios';

const TokenSelectList = ({ tokens, selectedToken, onSelectToken }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [validTokens, setValidTokens] = useState([]);
  const [tokenImages, setTokenImages] = useState({}); // 追加: トークン画像のキャッシュ

  // grantTokenがnullでないコインのみにフィルタリング
  useEffect(() => {
    const fetchTokenImage = async tokenAddress => {
      if (!tokenAddress || tokenImages[tokenAddress]) return;

      // APIのURLを画像URLとして直接使用
      const imageUrl = `${process.env.REACT_APP_API_URL}/api/tokens/${tokenAddress}/image`;

      // 状態を更新
      setTokenImages(prev => ({
        ...prev,
        [tokenAddress]: imageUrl,
      }));
    };

    const filtered = tokens.filter(token => token.grantToken);
    setValidTokens(filtered);

    // 追加: トークンの画像をロード
    filtered.forEach(token => {
      fetchTokenImage(token.grantToken);
    });
  }, [tokens, validTokens, tokenImages]);

  useEffect(() => {
    return () => {
      // 作成したすべてのURLオブジェクトを解放
      Object.values(tokenImages).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [tokenImages]);

  // フィルタリングされたトークンリスト
  const filteredTokens = validTokens.filter(token => {
    const name = token.name?.toLowerCase() || '';
    const symbol = token.symbol?.toLowerCase() || '';
    const grantToken = token.grantToken?.toLowerCase() || '';
    const pairType = token.pairType?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();

    return (
      name.includes(query) ||
      symbol.includes(query) ||
      grantToken.includes(query) ||
      pairType.includes(query)
    );
  });

  // トークンシンボルから最初の2文字を取得
  const getTokenInitials = symbol => {
    if (!symbol) return '??';
    return symbol.substring(0, 2).toUpperCase();
  };

  // トークンアイコンの背景色を生成（アドレスをもとにしたランダム色）
  const getTokenColor = address => {
    if (!address) return 'bg-blue-100';

    // アドレスの末尾6文字を取得してハッシュ化
    const hash = address.substring(address.length - 6);
    const hue = parseInt(hash, 16) % 360;

    // 明るめのパステルカラーを返す
    return `hsl(${hue}, 70%, 85%)`;
  };

  // アドレスを短縮
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      {/* 検索フィールド */}
      <div className="border-b border-gray-300 px-3 py-2 bg-gray-50">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="コイン名、シンボル、アドレスで検索..."
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* 選択されたトークン */}
      {selectedToken && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-300 dark:border-gray-600">
          <div className="flex items-center">
            {/* トークン画像があれば表示、なければイニシャル表示 */}
            {tokenImages[selectedToken.grantToken] ? (
              <img
                src={selectedToken.img}
                alt={selectedToken.symbol}
                className="w-8 h-8 rounded-full object-cover mr-3"
                onError={e => {
                  // 画像ロードエラー時はフォールバック
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}

            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${getTokenColor(
                selectedToken.grantToken
              )} text-gray-700 dark:text-gray-200 font-bold mr-3 ${
                tokenImages[selectedToken.grantToken] ? 'hidden' : 'flex'
              }`}
            >
              {getTokenInitials(selectedToken.symbol)}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{selectedToken.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedToken.symbol}
                  </div>
                </div>
                <button
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectToken(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* トークンリスト */}
      <div className="max-h-60 overflow-y-auto">
        {filteredTokens.length > 0 ? (
          <div>
            {filteredTokens.map(token => (
              <button
                key={token.grantToken}
                className={`w-full px-3 py-2 flex items-center hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  selectedToken?.grantToken === token.grantToken
                    ? 'bg-gray-100 dark:bg-gray-700'
                    : ''
                }`}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelectToken(token);
                }}
              >
                {/* トークン画像があれば表示、なければイニシャル表示 */}
                {/* {tokenImages[token.grantToken] ? ( */}
                <img
                  src={token.img}
                  alt={token.symbol}
                  className="w-8 h-8 rounded-full object-cover mr-3"
                  onError={e => {
                    // 画像ロードエラー時はフォールバック
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                {/* ) : null} */}

                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${getTokenColor(
                    token.grantToken
                  )} text-gray-700 dark:text-gray-200 font-bold mr-3 ${
                    // tokenImages[token.grantToken] ? 'hidden' : 'flex'
                    'hidden'
                  }`}
                >
                  {getTokenInitials(token.symbol)}
                </div>

                <div className="flex-1 text-left">
                  <div className="font-medium">{token.name}</div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      {token.symbol}
                      {token.pairType && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 rounded-full text-xs">
                          {token.pairType}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-400">
                      {shortenAddress(token.grantToken)}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500">コインが見つかりません</div>
        )}
      </div>
    </div>
  );
};

export default TokenSelectList;
